import { FunctionComponent } from 'react';
import { Attachment, User } from '@wix/answers-api';
import { useRouter } from 'next/router';
import { useTranslation } from '@wix/wix-i18n-config';
import moment from 'moment';
import 'moment/min/locales';
import { Avatar, Box, Text } from '@wix/design-system';
import { DATA_HOOKS } from '../../../dataHooks';
import { RenderHTML } from '@wix/cc-user-facing-common-components';
import css from './index.module.scss';

type TicketTimelineItemProps = {
  user: User;
  subject?: string;
  content: string;
  creationDate: number;
  isAgent: boolean;
  attachments?: Attachment[];
};

export const TicketTimelineItem: FunctionComponent<TicketTimelineItemProps> = ({
  user,
  subject,
  content,
  creationDate,
  isAgent,
  attachments,
}: TicketTimelineItemProps) => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const creationDateText = moment(creationDate)
    .locale(locale as string)
    .format('LT, LL');

  return (
    <Box direction="vertical" className={css.wrapper}>
      <Box gap="14px" verticalAlign="middle">
        <Avatar
          name={user.firstName}
          imgProps={{ src: user.profileImage }}
          className={css.avatar}
        />
        <Box direction="vertical" verticalAlign="middle" gap="4px" width="100%">
          <Text dataHook={DATA_HOOKS.TICKET_TIMELINE_ITEM_FIRSTNAME}>
            {user.firstName}
          </Text>
          <Box gap="10px">
            {isAgent && (
              <Text
                dataHook={DATA_HOOKS.TICKET_TIMELINE_ITEM_OFFICIAL_ANSWER}
                className={css.officialAnswer}
                size="small"
              >
                {t('ticket.official.answer')}
              </Text>
            )}
            <Text
              dataHook={DATA_HOOKS.TICKET_TIMELINE_ITEM_CREATION_DATE}
              size="small"
              className={css.creationDate}
            >
              {creationDateText}
            </Text>
          </Box>
        </Box>
      </Box>
      <Box
        direction="vertical"
        className={`${css.reply} ${isAgent && css.replyAgent}`}
      >
        <div className={`${css.triangle} ${isAgent && css.triangleAgent}`} />
        {subject && (
          <Text
            dataHook={DATA_HOOKS.TICKET_TIMELINE_ITEM_SUBJECT}
            className={css.subject}
          >
            {subject}
          </Text>
        )}
        <RenderHTML
          html={content}
          className={css.content}
          dataHook={DATA_HOOKS.TICKET_TIMELINE_ITEM_CONTENT}
        />
        {attachments && attachments?.length > 0 && (
          <Box gap="10px">
            {attachments?.map((attachment) => (
              <a
                href={attachment.url}
                key={attachment.url}
                className={css.attachment}
              >
                {attachment.url.substring(attachment.url.lastIndexOf('/') + 1)}
              </a>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};
